/**
 * The ONE financing-product normaliser for the portal (epic EM-1447).
 *
 * <h2>Why it exists</h2>
 * A financing queue row carries no `product` field: `ExecutionTaskDTO` is frozen and Rule 06 forbids
 * adding one. What it does carry is the `stage` label its handler emitted — e.g.
 * `"Stage 12 · KO Rules · Invoice Factoring"`. Every surface that needs to know which product a row
 * or a page is about therefore resolves it from a free-text-ish label, and every such surface must
 * resolve it THE SAME WAY. A second, slightly different matcher is how "Working Capital" ends up
 * counted under one chip and gating a different page.
 *
 * <h2>No silent default — deliberately</h2>
 * The static prototype's `resolveUwProduct()` defaulted to Working Capital when it could not tell.
 * The PRD flags that as a bug and BR-UW-013 makes the product gate mandatory with no silent default:
 * a page that guesses shows an underwriter the wrong product's rules. So this returns `null` for
 * anything it cannot recognise, and callers must decide what to do with "unknown" explicitly.
 *
 * Created here by EM-1454 (Story 1) because the workspace's product chips are its first consumer;
 * epic Story 2 (EM-1463) adds the `ProductGateComponent` alongside it in this same folder and
 * consumes this function — it does NOT introduce a second resolver (Rule 17).
 *
 * <h2>The deliberate IF/WCL ↔ FACTORING/WC asymmetry — documented in this ONE place</h2>
 * The **backend enum** (`FinancingProduct`, and every persisted row / API filter that speaks it) is
 * `FACTORING | WC | SDF`. The **PRD and the UI** — the product gate's segmented selector, the
 * `?product=` query parameter, `core/constants/products.ts` — say `IF | WCL | SDF`.
 *
 * Both vocabularies are real and neither can be dropped: renaming the enum would rewrite persisted
 * data, and renaming the UI codes would break every URL and the PRD's own language. So the canonical
 * portal type stays the backend one (that is what queue rows, API filters and every consumer already
 * carry), and the UI spelling is a thin display/URL layer: {@link toUiProduct} /
 * {@link fromUiProduct}. Every mapping between the two lives here, so no page hand-maps them.
 *
 * EM-1463 extended `resolveProduct` in place — with the `IF`/`WCL`/`invoice`/`smart`/`working`
 * aliases the gate needs — rather than adding a second resolver returning the UI codes. A second
 * resolver was the original subtask's shape and is exactly the duplication Rule 17 forbids.
 */

import { LENDOX_PRODUCTS } from '../../../core/constants/products';

/** The three financing products, as the backend `FinancingProduct` enum names them. */
export type FinancingProductCode = 'FACTORING' | 'WC' | 'SDF';

/** The same three products as the PRD / UI / `?product=` spell them. */
export type UiProductCode = 'IF' | 'WCL' | 'SDF';

/** Canonical → UI spelling. Total by construction. */
const UI_BY_CANONICAL: Readonly<Record<FinancingProductCode, UiProductCode>> = {
  FACTORING: 'IF',
  WC: 'WCL',
  SDF: 'SDF',
};

/**
 * The three products in the order the gate renders them, as UI codes.
 *
 * Derived from {@link LENDOX_PRODUCTS} keys rather than re-listed, so the gate cannot offer a product
 * the product table does not describe (Rule 17 — one product table).
 */
export const UI_PRODUCT_CODES: readonly UiProductCode[] = ['IF', 'WCL', 'SDF'];

/** The UI spelling of a canonical product code — for the `?product=` value and the gate's chips. */
export function toUiProduct(code: FinancingProductCode): UiProductCode {
  return UI_BY_CANONICAL[code];
}

/**
 * The canonical code for a UI/PRD product string, or `null` when it is not one of the three.
 *
 * A thin alias of {@link resolveProduct} kept for readability at call sites that genuinely hold a UI
 * code (a `?product=` value, a gate chip) rather than a free-text stage label — same function, same
 * no-default guarantee.
 */
export function fromUiProduct(raw: string | null | undefined): FinancingProductCode | null {
  return resolveProduct(raw);
}

/** The display name of a product, from the ONE product table. */
export function productDisplayName(code: FinancingProductCode): string {
  return LENDOX_PRODUCTS[toUiProduct(code)]?.name ?? toUiProduct(code);
}

/**
 * Recognition table: product code → the lower-cased needles that identify it.
 *
 * Covers BOTH shapes the label can take — the raw enum name (`FACTORING`, `WC`, `SDF`) and the
 * human label a handler may have written into the stage column (`Invoice Factoring`,
 * `Working Capital`, `Supply-Chain / Distributor Financing`), in English and Romanian, because the
 * stage label is produced server-side and is not translated.
 *
 * Order matters: the array is scanned in declaration order, so a longer / more specific needle must
 * come before a shorter one that it contains.
 */
const PRODUCT_NEEDLES: ReadonlyArray<readonly [FinancingProductCode, readonly string[]]> = [
  // `factor` subsumes `factoring` and the PRD's bare `FACTOR` alias; `factur` is the Romanian
  // "factură". Both are specific enough that no other financing label contains them.
  ['FACTORING', ['factor', 'factur', 'invoice']],
  [
    'SDF',
    [
      'supply-chain',
      'supply chain',
      'lant de aprovizionare',
      'distributor',
      'distribuitor',
      'distribution',
      'distributie',
      'smart',
    ],
  ],
  ['WC', ['working capital', 'working-capital', 'capital de lucru', 'working']],
];

/**
 * The product a stage label / product string refers to, or `null` when it cannot be told.
 *
 * @param raw the `stage` label of a queue row, a `?product=` query value, or a raw enum name
 */
export function resolveProduct(raw: string | null | undefined): FinancingProductCode | null {
  if (!raw) {
    return null;
  }
  const value = raw.trim();
  if (!value) {
    return null;
  }

  // 1. Exact enum name — the cheapest and least ambiguous case.
  const upper = value.toUpperCase();
  if (upper === 'FACTORING' || upper === 'WC' || upper === 'SDF') {
    return upper;
  }

  // 2. Short codes as WHOLE TOKENS of a composite label ("Stage 12 · KO Rules · WC"). Matching `wc`
  //    as a substring would fire on any word containing those two letters, so the label is split on
  //    its separators first and compared token-by-token.
  //
  //    `if` is the PRD/UI code for Invoice Factoring and is included here (EM-1463) so a `?product=IF`
  //    value resolves. It is matched as a WHOLE TOKEN only — a substring match would fire on
  //    "verification", "notify", … — and a stage label containing the English word "if" as its own
  //    token has never been emitted by a handler; the accepted risk is one-directional (it can only
  //    ever resolve a label toward Factoring, never away from a product it already matched, because
  //    the exact-enum check above runs first).
  const tokens = lower(value).split(/[^a-z0-9]+/).filter(Boolean);
  if (tokens.includes('wc') || tokens.includes('wcl')) {
    return 'WC';
  }
  if (tokens.includes('sdf')) {
    return 'SDF';
  }
  if (tokens.includes('if')) {
    return 'FACTORING';
  }

  // 3. Human labels, matched as substrings so word order and punctuation do not matter.
  const haystack = lower(value);
  for (const [code, needles] of PRODUCT_NEEDLES) {
    if (needles.some(needle => haystack.includes(needle))) {
      return code;
    }
  }
  return null;
}

/**
 * Lower-cases with the Romanian diacritics folded, so "Factură" matches the "factur" needle.
 *
 * The combining-mark range is written as a `̀-ͯ` escape rather than as literal characters:
 * literal combining marks in source are invisible in a diff and get mangled by editors that
 * normalise the file.
 */
function lower(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}
